import { PrizeTierMap } from '../constants';

export const promoCodeConstants = {
  /** The older first entry discount, which was superceeded after an AB test - see ENG-1735 */
  FIRST_ENTRY_DISCOUNT_CODE: 'FIRSTENTRY50OFF',
  FIRST_ORDER_DISCOUNT_CODE: 'FIRSTORDER50OFF',
} as const;

export const introOfferPromoCodes = [
  promoCodeConstants.FIRST_ENTRY_DISCOUNT_CODE,
  promoCodeConstants.FIRST_ORDER_DISCOUNT_CODE,
] as const;

export const isIntroOfferPromoCode = (
  promoCode?: string | null,
): promoCode is (typeof introOfferPromoCodes)[number] =>
  typeof promoCode === 'string' &&
  (introOfferPromoCodes as readonly string[]).includes(promoCode);

export const promoPrizeConstants = {
  /** The amount a player must have spent in order to get their consolation prize */
  CONSOLATION_PRIZE_SPEND_CRITERIA_PENCE: 500,
  CONSOLATION_PRIZE_CODE: 'CONSOLATION_PRIZE',
  WINWITHFRIENDS_PRIZE_CODE_MAP: {
    Treat1: 'WINWITHFRIENDS_TREAT1',
    Treat2: 'WINWITHFRIENDS_TREAT2',
    Treat3: 'WINWITHFRIENDS_TREAT3',
    Bronze: 'WINWITHFRIENDS_BRONZE',
    Silver: 'WINWITHFRIENDS_SILVER',
    Gold: 'WINWITHFRIENDS_GOLD',
    Platinum: 'WINWITHFRIENDS_PLATINUM',
    Diamond: 'WINWITHFRIENDS_DIAMOND',
    /** NOTE - in order to avoid confusion with boosted vs non-boosted 4+1 matches, we just
    give 4+1 non-boosted players the same reward as if their friend was boosted */
    DAYMAKER: 'WINWITHFRIENDS_DAYMAKERBOOST',
    DAYMAKERboost: 'WINWITHFRIENDS_DAYMAKERBOOST',
  } as const satisfies Record<keyof typeof PrizeTierMap, string>,
};

export type BrevoTemplateInfo =
  | number
  | {
      id: number;
      _emailDeprecated?: string;
    };

export const BREVO_TEMPLATE_MAP = {
  ACCOUNT_CLOSED: 541,

  WINNER_STANDARD: {
    id: 386,
    _emailDeprecated: 'This email is no longer used as of October 2025',
  },
  CONSOLATION_PRIZE: 430,

  FREE_DRAW_SUBSCRIPTION: 399,
  FREE_ENTRY_AWARDED: 460,

  TREAT_WIN_PREMIUM_0_1: 735,
  TREAT_WIN_PREMIUM_1_0: 734,
  TREAT_WIN_3: 419,
  TREAT_WIN_1_LITE_0_1: 705,
  TREAT_WIN_1_LITE_1_0: 706,
  TREAT_WIN_2_LITE: 707,
  TREAT_WIN_3_LITE: 708,

  CHOOSE_PRIZE_REMINDER_BRONZE: 435,
  CHOOSE_PRIZE_REMINDER_SILVER: 436,
  CHOOSE_PRIZE_REMINDER_GOLD: 437,

  PRIZE_WIN_BRONZE: 608,
  PRIZE_WIN_SILVER: 444,
  PRIZE_WIN_GOLD: 445,
  PRIZE_WIN_PLATINUM: 565,
  PRIZE_WIN_DIAMOND: 566,
  PRIZE_WIN_DAYMAKER: 709,
  PRIZE_WIN_DAYMAKERBOOST: 675,

  GET_EXCITED_TUESDAY_DRAW: 431,
  GET_EXCITED_FRIDAY_DRAW: 498,

  RESULTS_LIVE: 497, // Not to be implemented in DRIP
  LEADERBOARD_RESULTS_LIVE: 501,

  PURCHASE_TOKENS: 440,
  TOKENS_EXPIRING: 442,

  TREAT_EXPIRED: 651,

  POSTAL_ENTRY_CONFIRMATION: 441, // previously ALTERNATIVE_ENTRY_CONFIRMATION

  START_WEEKLY_DRAW_SUBSCRIPTION: 797,
  /** Remove template 434 after DRIP is live, as it won't be needed, and also remove it from Brevo */
  OLD_NON_DRIP_SUBSCRIPTION_BOOST_CONFIRMATION: 434,
  SUBSCRIPTION_BOOST_CONFIRMATION: 511,
  SUBSCRIPTION_CHANGE_BOOST_TO_BOTH_DAYS_CONFIRMATION: 600,
  SUBSCRIPTION_TOP_UP_CONFIRMATION: 464,
  SUBSCRIPTION_SNOOZED: 454,
  SUBSCRIPTION_CANCELLED: 456,
  SUBSCRIPTION_FAILED: 458,
  SUBSCRIPTION_UPDATED: 432,
  SUBSCRIPTION_DOWNGRADED_TO_FREE: 457,
  SUBSCRIPTION_DOWNGRADED_TO_LITE: 583,
  SUBSCRIPTION_PAYMENT_FAILED: 459,

  PAYMENT_METHOD_UPDATED: 494,
  CREDIT_CARD_WARNING: 577,

  GIFT_CLOUD_REWARD_REDEMPTION: 465,
  PRIZE_CHOICE_CONFIRMATION: 449,
  PRIZE_AWARDED: 462,
  TREE_REDEMPTION_CONFIRMATION: 593,

  ACCOUNT_INFO_UPDATED: 492,
  PASSWORD_UPDATED: 493,
  PASSWORD_RESET_LINK: 495,

  TOKEN_REDEMPTION_DAILY_CHALLENGER: 502,
  TOKEN_REDEMPTION_DAILY_POUND_RAFFLE: 512,
  TOKEN_REDEMPTION_SUBSCRIBER_RAFFLE: 520,
  TOKEN_REDEMPTION_MINILOTTO: 525,

  TODAY_IN_TOKEN_TOWN: 488,
  TOKEN_REMINDER_GENERIC: 554,
  TOKEN_REMINDER_2: 555,

  DAILY_CHALLENGER_RESULTS_LIVE: 524,
  POUND_RAFFLE_WINNER: 523,
  POUND_RAFFLE_RESULTS_LIVE: 556,
  FIXED_ODDS_RAFFLE_WINNER: 530,
  MINILOTTO_WINNER: 529,
  MINILOTTO_RESULTS_LIVE: 528,
  MONTHLY_GIVEAWAY_WINNER: 644,

  WINNER_CONTENT_REQUEST: 453,

  NEWSLETTER_SIGN_UP: 543,
  HAPPY_BIRTHDAY: 539,

  // old referral scheme
  REFERRER_REWARD_24TOKENS: 532,
  REFERRAL_REQUEST_POST_SIGNUP_1: 536,
  REFERRAL_REQUEST_POST_SIGNUP_2: 535,
  REFERRAL_REQUEST_POST_WIN: 534,

  // Win with Friends referral scheme
  WIN_WITH_FRIENDS_POST_SIGNUP_1: 747,
  WIN_WITH_FRIENDS_POST_SIGNUP_2: 763,
  WIN_WITH_FRIENDS_POST_SIGNUP_3: 764,

  WIN_WITH_FRIENDS_ENDING_PLAYER_LEAVING: 766,
  WIN_WITH_FRIENDS_ENDING_FRIEND_LEAVING_SENT_TO_REFERRER: 765,
  WIN_WITH_FRIENDS_ENDING_REFERRER_LEAVING_SENT_TO_FRIENDS: 784,

  WIN_WITH_FRIENDS_SHARED_WIN: 768,

  PRIZE_NOT_AVAILABLE_AND_REINSTATED: 806,
  PRIZE_VIA_TOKEN_REDEMPTION_NOT_AVAILABLE_AND_REFUNDED: 805,
} as const satisfies Record<string, BrevoTemplateInfo>;

export type BrevoTemplateName = keyof typeof BREVO_TEMPLATE_MAP;

export const CmsTypeSlugs = ['winner', 'winner-polaroid', 'review'] as const;
export type TCmsTypeSlug = (typeof CmsTypeSlugs)[number];
